import { Link } from "react-router-dom";
import { Page, H1, H2, H3, Lead, P, UL, LegalValue, Notice, Toc, PublisherCountry, DocVersion } from "../../public/primitives";
import { legal } from "../../../config/legal";

export const PrivacyPage = () => (
  <Page testid="privacy-page">
    <H1>Politique de confidentialité</H1>
    <Lead>
      Comment SecurePass traite les données selon les usages du service. Version <DocVersion value={legal.privacyVersion} /> —
      dernière mise à jour {legal.lastUpdated}.
    </Lead>

    <Toc
      items={[
        { id: "responsable", label: "Responsable du traitement" },
        { id: "donnees", label: "Données traitées" },
        { id: "finalites", label: "Finalités" },
        { id: "destinataires", label: "Destinataires" },
        { id: "sous-traitants", label: "Sous-traitants" },
        { id: "conservation", label: "Conservation" },
        { id: "securite", label: "Sécurité" },
        { id: "test-mdp", label: "Test de mot de passe" },
        { id: "droits", label: "Vos droits" },
        { id: "exercice", label: "Exercice des droits" },
        { id: "transferts", label: "Transferts" },
        { id: "maj", label: "Mises à jour" },
      ]}
    />

    <H2 id="responsable">Responsable du traitement</H2>
    <P>
      Le responsable du traitement est <b>{legal.publisherName}</b> (<PublisherCountry />). Coordonnées
      de contact « données personnelles » : <LegalValue value={legal.privacyEmail} />. À défaut, utilisez
      la <Link to="/contact" className="text-emerald-600 underline">page Contact</Link>.
    </P>

    <H2 id="donnees">Données traitées</H2>
    <H3>Utilisateurs anonymes</H3>
    <P>
      Aucun compte n'est requis. Des métadonnées techniques strictement nécessaires à la protection du
      service et au fonctionnement d'un partage sont traitées (voir ci-dessous). Le contenu partagé est
      chiffré côté navigateur.
    </P>
    <H3>Utilisateurs avec compte</H3>
    <UL>
      <li>Données de compte : identité, e-mail, société, données d'abonnement.</li>
      <li>Données d'usage liées à l'espace de travail (historique de partages, membres).</li>
    </UL>
    <H3>Métadonnées techniques</H3>
    <UL>
      <li>Date de création, expiration et statut d'un partage.</li>
      <li>Informations anti-abus pseudonymisées, conservées pour une durée courte.</li>
      <li>
        Lors de l'utilisation du formulaire de contact, des informations techniques telles que
        l'adresse IP peuvent être enregistrées pour la sécurité et la prévention des abus. Durée de
        conservation à définir avant production commerciale.
      </li>
    </UL>
    <H3>Contenu chiffré</H3>
    <P>
      Le contenu des secrets et des réponses est traité sous forme chiffrée. SecurePass ne reçoit pas la
      clé de déchiffrement et ne peut donc pas lire ce contenu.
    </P>

    <H2 id="finalites">Finalités</H2>
    <UL>
      <li>Fournir le service de partage et de demande d'informations.</li>
      <li>Assurer la sécurité et prévenir les abus.</li>
      <li>Gérer les comptes, espaces de travail et abonnements.</li>
    </UL>

    <H2 id="destinataires">Destinataires</H2>
    <P>
      Les données sont destinées à {legal.publisherName} et, le cas échéant, à ses prestataires
      techniques strictement nécessaires au fonctionnement du service (voir la
      <Link to="/sous-traitants" className="text-emerald-600 underline"> liste des sous-traitants</Link>).
    </P>

    <H2 id="sous-traitants">Sous-traitants</H2>
    <P>
      La liste des sous-traitants réellement actifs est publiée sur la page dédiée. Aucun prestataire
      non confirmé n'y figure.
    </P>

    <H2 id="conservation">Conservation</H2>
    <P>
      Les durées de conservation figurent sur la page
      <Link to="/securite" className="text-emerald-600 underline"> Sécurité & Centre de confiance</Link>.
      Pour toute durée non encore arrêtée dans le cadre commercial : <LegalValue value={null} />.
    </P>

    <H2 id="securite">Sécurité</H2>
    <P>
      Chiffrement côté navigateur, clé séparée du contenu, lecture unique, expiration automatique et
      mesures HTTP de durcissement. Voir la page Sécurité pour le détail.
    </P>

    <H2 id="test-mdp">Test de mot de passe</H2>
    <P>
      La page « Tester un mot de passe » propose une analyse de robustesse locale et une vérification
      facultative d'exposition dans Pwned Passwords (Have I Been Pwned). Pour cette fonctionnalité :
    </P>
    <UL>
      <li>le mot de passe n'est jamais envoyé ;</li>
      <li>l'empreinte SHA-1 complète n'est jamais envoyée ;</li>
      <li>seul le préfixe de 5 caractères de cette empreinte est transmis ;</li>
      <li>la requête est déclenchée uniquement sur action de l'utilisateur ;</li>
      <li>Have I Been Pwned peut recevoir les informations réseau habituelles de la connexion, notamment l'adresse IP publique.</li>
    </UL>

    <H2 id="droits">Vos droits</H2>
    <P>
      Selon la réglementation applicable, vous pouvez disposer de droits d'accès, de rectification,
      d'effacement, d'opposition et de limitation.
    </P>

    <H2 id="exercice">Exercice des droits</H2>
    <P>
      Adressez votre demande à <LegalValue value={legal.privacyEmail} /> ou via la
      <Link to="/contact" className="text-emerald-600 underline"> page Contact</Link>.
    </P>

    <H2 id="transferts">Transferts éventuels</H2>
    <P>
      Les informations relatives à d'éventuels transferts de données seront précisées une fois la liste
      des sous-traitants et la localisation d'hébergement confirmées : <LegalValue value={null} />.
    </P>

    <H2 id="maj">Mises à jour de la politique</H2>
    <P>
      Cette politique peut évoluer. La version et la date de mise à jour figurent en tête de page.
    </P>

    <Notice testid="privacy-work-note">
      Version de travail à valider juridiquement avant production commerciale. Les éléments marqués « à
      compléter » ne sont pas inventés.
    </Notice>
  </Page>
);

export default PrivacyPage;
